import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  IconButton, 
  Typography, 
  Box, 
  FormControl, 
  FormControlLabel, 
  RadioGroup, 
  Radio,
  useTheme,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useCalculator } from '../../../contexts/CalculatorContext';

/**
 * Dialog for Business Phone settings
 */
const BusinessPhoneSettingsDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const { 
    businessPhoneType, 
    setBusinessPhoneType,
    businessPhoneUsers,
    setBusinessPhoneUsers,
    fmcUsers,
    setFmcUsers,
    hasBusinessPhoneCustomSettings,
    setHasBusinessPhoneCustomSettings,
    handleApplyBusinessPhoneSettings,
    region,
    veryCustomProductDiscounts,
    handleUpdateVeryCustomProductDiscount
  } = useCalculator();
  
  // Перевіряємо, чи регіон - Україна
  const isUkraineRegion = region === 'ua' || 
                        String(region).toLowerCase().includes('ukr') || 
                        String(region).toLowerCase().includes('ua') || 
                        String(region).toLowerCase() === 'ukraine';
  
  // Local state for settings
  const [localBusinessPhoneType, setLocalBusinessPhoneType] = React.useState(businessPhoneType);
  
  // Advanced Settings state
  const currentDiscount = veryCustomProductDiscounts['business_phone'] || 0;
  const [isAdvancedMode, setIsAdvancedMode] = React.useState(currentDiscount > 0);
  const [localDiscount, setLocalDiscount] = React.useState(currentDiscount);
  const [discountString, setDiscountString] = React.useState(String(currentDiscount));
  
  // Reset local state when dialog opens
  React.useEffect(() => {
    if (open) {
      setLocalBusinessPhoneType(businessPhoneType);
      const currentDiscount = veryCustomProductDiscounts['business_phone'] || 0;
      setLocalDiscount(currentDiscount);
      setDiscountString(String(currentDiscount));
      setIsAdvancedMode(currentDiscount > 0);
    }
  }, [open, businessPhoneType, veryCustomProductDiscounts]);

  // Handle keyboard shortcuts for advanced mode
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+Shift+C (PC) or Cmd+Shift+C (Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'C') {
        event.preventDefault();
        setIsAdvancedMode(prev => !prev);
      }
    };
    
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);
  
  // Handle double click on title for advanced mode
  const handleTitleDoubleClick = () => {
    setIsAdvancedMode(prev => !prev);
  };

  // Handle discount input changes
  const handleDiscountChange = (e) => {
    setDiscountString(e.target.value);
  };

  // Handle blur event - convert to number and validate (0-100%)
  const handleDiscountBlur = () => {
    const numValue = Number(discountString);
    if (isNaN(numValue) || numValue < 0) {
      setLocalDiscount(0);
      setDiscountString('0');
    } else if (numValue > 100) {
      setLocalDiscount(100);
      setDiscountString('100');
    } else {
      setLocalDiscount(numValue);
      setDiscountString(String(numValue));
    }
  };
  
  // Handle apply settings
  const handleApply = () => {
    // Для регіонів, відмінних від України, завжди встановлюємо тип "business-phone"
    const finalBusinessPhoneType = isUkraineRegion ? localBusinessPhoneType : 'business-phone';
    
    // Якщо тип не FMC, встановлюємо fmcUsers = 0
    let finalFmcUsers;
    if (finalBusinessPhoneType === 'fmc') {
      // Для standalone FMC мінімум 6 користувачів
      finalFmcUsers = Math.max(fmcUsers, 6);
    } else if (finalBusinessPhoneType === 'business-phone-fmc') {
      // Для комбінованого мінімуму немає
      finalFmcUsers = fmcUsers;
    } else {
      // Для business-phone only
      finalFmcUsers = 0;
    }
    
    // Apply very custom product discount
    handleUpdateVeryCustomProductDiscount('business_phone', localDiscount);
    
    // Check if there are any actual custom settings (not counting discount when it's 0)
    const hasAnyRealCustomSettings = (finalBusinessPhoneType !== 'business-phone') || 
                                     (finalFmcUsers > 0) ||
                                     (localDiscount > 0);
    
    // Оновлюємо FMC users в контексті якщо змінилося значення
    if (finalFmcUsers !== fmcUsers) {
      setFmcUsers(finalFmcUsers);
    }
    
    // Використовуємо обробник з контексту
    handleApplyBusinessPhoneSettings({
      businessPhoneType: finalBusinessPhoneType,
      businessPhoneUsers: businessPhoneUsers,
      fmcUsers: finalFmcUsers,
      hasBusinessPhoneCustomSettings: hasAnyRealCustomSettings
    });
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: theme.spacing(1),
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle 
        onDoubleClick={handleTitleDoubleClick}
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#f9f9f9',
          py: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          '& .MuiTypography-root': {
            fontWeight: 500,
            fontSize: 18
          }
        }}
      >
        {t('calculator.businessPhoneSettings', 'Business Phone Settings')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('calculator.businessPhoneType', 'Business Phone Type')}
        </Typography>
        
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <RadioGroup
            value={localBusinessPhoneType}
            onChange={(e) => setLocalBusinessPhoneType(e.target.value)}
          >
            <FormControlLabel 
              value="business-phone" 
              control={<Radio />} 
              label={t('calculator.businessPhoneOnly', 'Business Phone')} 
            />
            {isUkraineRegion && (
              <>
                <FormControlLabel 
                  value="fmc" 
                  control={<Radio />} 
                  label={t('calculator.fmcOnly', 'FMC (Fixed Mobile Convergence)')} 
                />
                <FormControlLabel 
                  value="business-phone-fmc" 
                  control={<Radio />} 
                  label={t('calculator.businessPhoneFmc', 'Business Phone + FMC')} 
                />
              </>
            )}
          </RadioGroup>
        </FormControl>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('calculator.businessPhonePricing', 'Business Phone: €20 per user')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('calculator.fmcPricing', 'FMC: €2.7 per user')}
        </Typography>

        {/* Advanced Settings */}
        {isAdvancedMode && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Accordion 
              defaultExpanded 
              elevation={0}
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: 1,
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: 'rgba(255, 152, 0, 0.08)',
                  '& .MuiAccordionSummary-content': { 
                    alignItems: 'center' 
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ color: '#FF9800', fontWeight: 600 }}>
                  {t('calculator.advancedSettings', 'Advanced Settings')}
                </Typography>
              </AccordionSummary>
              
              <AccordionDetails sx={{ pt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('calculator.veryCustomProductDiscount', 'Custom Product Discount')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('calculator.customDiscountNote', 'Applied after bundle discounts (0-100%)')}
                  </Typography>
                  
                  <TextField
                    size="small"
                    type="number"
                    value={discountString}
                    onChange={handleDiscountChange}
                    onBlur={handleDiscountBlur}
                    InputProps={{
                      endAdornment: '%',
                      inputProps: { min: 0, max: 100, step: 1 }
                    }}
                    sx={{ width: 120 }}
                    helperText={localDiscount > 0 ? `${localDiscount}% discount applied` : 'No discount'}
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary">
                  {t('calculator.advancedSettingsHint', 'Access: Double-click title or Ctrl+Shift+C')}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button 
          onClick={handleApply} 
          variant="contained" 
          sx={{ 
            bgcolor: '#00C853', 
            '&:hover': { bgcolor: '#00B34A' } 
          }}
        >
          {t('common.apply', 'Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessPhoneSettingsDialog;
